/**
 * Usage-based billing display. Pricing is region-specific:
 * Canada → CAD $1.00; United States → USD $1.00.
 */

import {
  DEFAULT_PRICING,
  getCachedBillingProfile,
  resolvePricingForCountry,
} from '../utils/billingRegion';

function getRuntimeBilling() {
  if (typeof window !== 'undefined' && window.__RETECHSYS_RUNTIME_CONFIG__) {
    const c = window.__RETECHSYS_RUNTIME_CONFIG__;
    return {
      price: c.pricePerTransactionDollars,
      currency: c.billingCurrency,
    };
  }
  return { price: null, currency: null };
}

export function getBillingCurrency(fallbackCountryCode = null) {
  const cached = getCachedBillingProfile();
  if (cached?.currency) {
    return cached.currency;
  }
  if (fallbackCountryCode) {
    return resolvePricingForCountry(fallbackCountryCode).currency;
  }
  const runtime = getRuntimeBilling();
  const raw = runtime.currency || process.env.REACT_APP_BILLING_CURRENCY || 'cad';
  const normalized = String(raw).trim().toLowerCase();
  return normalized === 'usd' ? 'usd' : 'cad';
}

export function getPricePerTransactionDollars(fallbackCountryCode = null) {
  const cached = getCachedBillingProfile();
  if (typeof cached?.pricePerTransactionDollars === 'number') {
    return cached.pricePerTransactionDollars;
  }
  if (fallbackCountryCode) {
    return resolvePricingForCountry(fallbackCountryCode).pricePerTransactionDollars;
  }
  const runtime = getRuntimeBilling();
  const raw = runtime.price ?? process.env.REACT_APP_PRICE_PER_TRANSACTION_DOLLARS;
  const parsed = raw !== undefined && raw !== '' ? parseFloat(raw) : 1;
  if (Number.isNaN(parsed) || parsed < 0) {
    return 1;
  }
  return parsed;
}

export function formatUsageMoney(amount, currency = getBillingCurrency()) {
  const num = parseFloat(amount);
  const value = Number.isNaN(num) ? 0 : num;
  return new Intl.NumberFormat(currency === 'usd' ? 'en-US' : 'en-CA', {
    style: 'currency',
    currency: currency === 'usd' ? 'USD' : 'CAD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

export function formatPricePerTransaction(options = {}) {
  const { countryCode, currency, pricePerTransactionDollars } = options;
  if (typeof pricePerTransactionDollars === 'number' && currency) {
    return formatUsageMoney(pricePerTransactionDollars, currency);
  }
  if (countryCode) {
    const pricing = resolvePricingForCountry(countryCode);
    return formatUsageMoney(pricing.pricePerTransactionDollars, pricing.currency);
  }
  return formatUsageMoney(
    getPricePerTransactionDollars(countryCode),
    getBillingCurrency(countryCode)
  );
}

export const BILLABLE_TRANSACTION_STATUSES = new Set([
  'completed',
  'generated',
  'success',
  'paid',
]);

export function isBillableTransactionStatus(status) {
  const normalized = (status || 'completed').toLowerCase();
  return BILLABLE_TRANSACTION_STATUSES.has(normalized);
}

export function isBillableTransaction(transaction) {
  if (!transaction) return false;
  if (transaction.billable === false || transaction.billable === 'false') {
    return false;
  }
  return isBillableTransactionStatus(transaction.status);
}

export function getTransactionUsageFee(transaction) {
  if (!transaction) return 0;
  if (typeof transaction.usage_fee === 'number') {
    return transaction.usage_fee;
  }
  if (!isBillableTransaction(transaction)) return 0;
  return getPricePerTransactionDollars();
}

export function getBillingTermsSummary(options = {}) {
  const { countryCode } = options;
  if (countryCode) {
    const pricing = resolvePricingForCountry(countryCode);
    const price = formatUsageMoney(
      pricing.pricePerTransactionDollars,
      pricing.currency
    );
    return {
      priceFormatted: price,
      perTransactionText: `${price} per completed transaction (${pricing.regionLabel})`,
      monthlyBillingText:
        'All usage for a calendar month is combined into a single monthly invoice on the 2nd of the following month (04:45 UTC). Stripe charges your saved payment method automatically — not per transaction.',
      currency: pricing.currency,
      countryCode: pricing.countryCode,
    };
  }

  const cadPrice = formatUsageMoney(DEFAULT_PRICING.CA.pricePerTransactionDollars, 'cad');
  const usdPrice = formatUsageMoney(DEFAULT_PRICING.US.pricePerTransactionDollars, 'usd');

  return {
    priceFormatted: `${cadPrice} (Canada) / ${usdPrice} (United States)`,
    perTransactionText: `${cadPrice} per completed transaction in Canada, ${usdPrice} in the United States`,
    monthlyBillingText:
      'All usage for a calendar month is combined into a single monthly invoice on the 2nd of the following month (04:45 UTC). Stripe charges your saved payment method automatically — not per transaction.',
    currency: null,
    countryCode: null,
  };
}

export function formatRegionalPricingLine() {
  const summary = getBillingTermsSummary();
  return summary.perTransactionText;
}
